import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
  Image,
  Modal,
  useWindowDimensions,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import * as Location from 'expo-location';
import * as ImagePicker from 'expo-image-picker';
import * as FileSystem from 'expo-file-system/legacy';
import { Header, FormHeader, ScreenLayout, colors, spacing, typography } from '../components';
import { BusinessUserPicker } from '../components/BusinessUserPicker';
import { ConfirmationModal } from '../components/Modal';
import { LiveVisitMap, LiveVisitTravelMode } from '../components/LiveVisitMap';
import { NotesSection } from '../components/NotesSection';
import { ContactSummaryCard } from '../components/ContactSummaryCard';
import { ContactEditForm } from '../components/ContactEditForm';
import { designTokens } from '../theme/design-system';
import { useAppContext } from '../contexts/AppContext';
import { useLocale } from '../contexts/LocaleContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { apiService, Visit, VisitOutcome, VisitPhoto } from '../services/api';
import { getImageUrlSync } from '../utils/imageProxy';
import { useAppStore } from '../store/appStore';
import { useThemedStyles } from '../hooks/useThemedStyles';

type ExecutePhase = 'pre-start' | 'in-progress' | 'ending';

interface EffectiveConfig {
  outcomes: VisitOutcome[];
  enforcement: {
    location: { enabled: boolean; radiusMeters: number; maxAccuracyMeters: number; allowOverrideWithReason: boolean };
    photos: { requireOnStart: boolean; requireOnEnd: boolean; cameraOnly: boolean; minPhotosOnStart: number; minPhotosOnEnd: number; maxPhotos: number };
  };
}

const haversineMeters = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371000;
  const toRad = (v: number) => (v * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

// Shortest distance (meters) from a point to any point on a route's line —
// used for off-route detection (see the OFF_ROUTE_METERS rebaseline logic
// below). Cheap point-to-vertex distance (not point-to-segment), which is
// fine at the spacing of real route geometry (points a few meters to a few
// tens of meters apart) — the small overestimate near long straight
// segments never matters at the ~70m threshold this is used with.
const nearestDistanceToRouteMeters = (point: { latitude: number; longitude: number }, route: Array<[number, number]>): number => {
  let nearest = Infinity;
  for (const [lat, lon] of route) {
    const d = haversineMeters(point.latitude, point.longitude, lat, lon);
    if (d < nearest) nearest = d;
  }
  return nearest;
};

const formatElapsed = (ms: number): string => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
};

const formatDistanceLabel = (meters: number): string =>
  meters >= 1000 ? `${(meters / 1000).toFixed(1)} km` : `${Math.round(meters)}m`;

// Maps OSRM's own maneuver type/modifier vocabulary to an icon — kept as a
// lookup with a generic fallback (rather than an exhaustive switch) so an
// OSRM maneuver type/modifier this doesn't recognize still renders a
// reasonable arrow instead of nothing.
const maneuverIconName = (maneuverType: string, maneuverModifier?: string): keyof typeof Ionicons.glyphMap => {
  if (maneuverType === 'arrive') return 'flag-outline';
  if (maneuverType === 'depart') return 'navigate-outline';
  if (maneuverType === 'roundabout' || maneuverType === 'rotary') return 'sync-outline';
  switch (maneuverModifier) {
    case 'left':
    case 'sharp left':
      return 'arrow-back-outline';
    case 'right':
    case 'sharp right':
      return 'arrow-forward-outline';
    case 'slight left':
      return 'arrow-undo-outline';
    case 'slight right':
      return 'arrow-redo-outline';
    case 'uturn':
      return 'return-up-back-outline';
    default:
      return 'arrow-up-outline';
  }
};

/**
 * Classic full-screen execution (turn-by-turn navigation panel). The visit
 * page (VisitExecuteScreen below) now runs the visit inside the contact
 * profile; this stays reachable from there for step-by-step navigation.
 */
export const VisitExecuteClassicScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, routeParams, showNotification, activeTab, setActiveTab } = useAppContext();
  const { t } = useLocale();
  const { currentBusiness, user: currentUser } = useAppStore();
  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);
  const { width: windowWidth } = useWindowDimensions();
  // Platform.OS === 'web' alone doesn't distinguish a desktop browser tab
  // from a phone's mobile browser (React Native Web reports 'web' for
  // both) — the side-by-side directions layout needs real desktop width to
  // make sense, so it's gated on the same breakpoint DashboardScreen.tsx
  // already uses for its own desktop/tablet/mobile layout switch, not just
  // platform alone.
  const isDesktopWeb = Platform.OS === 'web' && windowWidth >= designTokens.breakpoints.desktop;
  // The mobile fullscreen execution Modal (below) renders its own overlay/
  // sheet directly against the device screen edges — a plain Modal gets NO
  // safe-area handling for free the way a normal navigator screen does, so
  // mobileTopOverlay/mobileSheet need these insets explicitly to clear the
  // notch/Dynamic Island (top) and home-indicator (bottom) rather than
  // sitting at a fixed spacing.md/0 that ignores the device's actual safe
  // area — confirmed real bug: content was rendering under the notch and
  // right against the home-indicator gesture area.
  const insets = useSafeAreaInsets();

  const visitId: string | undefined = routeParams?.visitId;

  const [visit, setVisit] = useState<Visit | null>(null);
  const [config, setConfig] = useState<EffectiveConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [phase, setPhase] = useState<ExecutePhase>('pre-start');

  // Contact's own persistent notes (across every interaction, not just this
  // visit) — mirrors VisitDetailScreen's identical Contact Notes section,
  // now also available during actual execution, not just pre-start.
  const [contact, setContact] = useState<any>(null);
  useEffect(() => {
    if (!visit?.contactId) {
      setContact(null);
      return;
    }
    apiService.getParentContactById(visit.contactId)
      .then((res) => setContact(res?.data ?? null))
      .catch((err) => console.warn('[VisitExecute] loadContact error:', err));
  }, [visit?.contactId]);
  const contactName: string = contact?.profile?.name || t('visits.detail.unknownContact');

  const handleAddContactNote = useCallback(async (content: string) => {
    if (!contact?._id || !content.trim()) return;
    try {
      const result = await apiService.addContactNote(contact._id, content.trim());
      if (result.statusCode === 1000 && result.data) {
        setContact((prev: any) => (prev ? { ...prev, notes: result.data.notes || [] } : prev));
      }
    } catch (err) {
      console.warn('[VisitExecute] addContactNote error:', err);
    }
  }, [contact?._id]);

  const handleDeleteContactNote = useCallback(async (noteId: string | number) => {
    if (!contact?._id) return;
    try {
      const result = await apiService.deleteContactNote(contact._id, String(noteId));
      if (result.statusCode === 1000 && result.data) {
        setContact((prev: any) => (prev ? { ...prev, notes: result.data.notes || [] } : prev));
      }
    } catch (err) {
      console.warn('[VisitExecute] deleteContactNote error:', err);
    }
  }, [contact?._id]);

  const [currentPosition, setCurrentPosition] = useState<{ latitude: number; longitude: number; accuracyMeters?: number } | null>(null);
  const [isFetchingLocation, setIsFetchingLocation] = useState(false);

  const [startPhotos, setStartPhotos] = useState<VisitPhoto[]>([]);
  const [endPhotos, setEndPhotos] = useState<VisitPhoto[]>([]);
  const [isCapturingPhoto, setIsCapturingPhoto] = useState(false);
  // expo-image-picker's launchCameraAsync is unreliable on web (confirmed
  // elsewhere in this app — ProductScannerScreen.tsx's own doc comment for
  // its identical CameraView fallback) — it can silently fall through to
  // the browser's plain file picker, which is exactly the "camera only, not
  // upload" requirement being violated. Web uses an embedded expo-camera
  // CameraView + takePictureAsync instead; native keeps launchCameraAsync,
  // which works correctly there.
  const [CameraView, setCameraView] = useState<any>(null);
  const [showCameraModal, setShowCameraModal] = useState<'start' | 'end' | null>(null);
  const [cameraPermissionGranted, setCameraPermissionGranted] = useState<boolean | null>(null);
  const cameraRef = useRef<any>(null);

  const [overrideReason, setOverrideReason] = useState('');
  const [geofenceError, setGeofenceError] = useState<{ distanceMeters: number; radiusMeters: number } | null>(null);
  // Set when the backend rejects Start because this agent already has a
  // DIFFERENT visit in_progress (one active visit at a time) — holds that
  // other visit's id so the modal's action can jump straight to it.
  const [ongoingVisitConflictId, setOngoingVisitConflictId] = useState<string | null>(null);
  const [isStarting, setIsStarting] = useState(false);

  const [elapsedNow, setElapsedNow] = useState(Date.now());
  const [noteText, setNoteText] = useState('');
  const [isAddingNote, setIsAddingNote] = useState(false);

  // Live in-app navigation (Talabat/Uber-style directions view) — the
  // agent's position is watched continuously (not one-off fetches like
  // pre-start's currentPosition) while the visit is in progress, and
  // live-directions is re-fetched from the backend on each update so the
  // route line/distance/ETA stay current as the agent actually moves.
  const [liveAgentPosition, setLiveAgentPosition] = useState<{ latitude: number; longitude: number } | null>(null);
  const [liveDirections, setLiveDirections] = useState<{
    distanceMeters: number;
    durationSeconds?: number;
    geometry: Array<[number, number]>;
    engineFallbackReason?: string;
    // Backend re-resolves this LIVE from the contact's current profile on
    // every poll (for a not-yet-started visit) — captured here so the map
    // pin can follow a contact's location edit without needing a screen
    // reload. Confirmed real bug: this field was fetched every ~3s along
    // with distance/geometry but silently discarded, while the map's
    // destination prop kept reading the one-time-fetched visit.targetLocation
    // instead — the backend was already returning fresh data the client
    // never looked at.
    destination?: { latitude: number; longitude: number; address?: string };
    steps?: Array<{
      maneuverType: string;
      maneuverModifier?: string;
      name: string;
      distanceMeters: number;
      durationSeconds: number;
      location: [number, number];
    }>;
    // Other candidate routes OSRM offered for this same trip — does NOT
    // include the primary route (that's this object's own top-level
    // distanceMeters/geometry), matching the backend's own LiveDirections
    // shape. Empty/undefined for the haversine engine or when OSRM found
    // only one reasonable path.
    alternatives?: Array<{
      distanceMeters: number;
      durationSeconds?: number;
      geometry: Array<[number, number]>;
    }>;
  } | null>(null);
  // 0 = the primary route (liveDirections' own top-level fields), 1+ = index
  // into liveDirections.alternatives + 1 — i.e. selectedRouteIndex - 1 is the
  // alternatives array index. Reset to 0 (back to primary) whenever the
  // phase changes or a fresh baseline is captured, so switching visits or
  // restarting navigation doesn't carry over a stale selection.
  const [selectedRouteIndex, setSelectedRouteIndex] = useState(0);
  const locationWatchRef = useRef<Location.LocationSubscription | null>(null);
  // Fixed — the travel-mode picker was removed from visit execution (agents
  // always drive between visits); route/ETA calculations still take a mode
  // param, so this constant keeps that plumbing unchanged.
  const travelMode: LiveVisitTravelMode = 'driving';
  // Baseline for the ETA progress bar — the FIRST distance reported once
  // in-progress starts, held fixed afterward so the bar depletes toward 0
  // as remaining distance shrinks, rather than recomputing against a
  // moving target (which would make the fill jump around instead of
  // monotonically progressing). Reset on travel-mode change since
  // switching e.g. driving->walking recalculates the whole route against
  // a different baseline distance.
  const [initialDistanceMeters, setInitialDistanceMeters] = useState<number | null>(null);
  const initialDistanceBaselineKeyRef = useRef<string | null>(null);
  // The FULL route geometry captured once at the same moment as
  // initialDistanceMeters (same baselineKey, see below) — kept static
  // afterward so LiveVisitMap can grey out the portion behind the agent
  // as they advance along it, the way a normal navigation app's progress
  // line works. Distinct from liveDirections.geometry, which the backend
  // keeps re-trimming to "remaining route only" on every GPS tick.
  const [originalRouteGeometry, setOriginalRouteGeometry] = useState<Array<[number, number]> | null>(null);
  const [isMapFullscreen, setIsMapFullscreen] = useState(false);
  // Whether the full-screen map is currently open (mobile) — shared by BOTH
  // pre-start and in-progress, collapsed by default in both (reachable
  // on-demand via a "View on DISLIX Maps" button, dismissible via a close
  // button when open). Pre-start used to default this open automatically —
  // confirmed real bug: landing on this screen (from either Start Visit or
  // Visit Detail's own "View on DISLIX Maps", which now opens the SAME map
  // as its own Modal directly on Detail) immediately re-showed the map the
  // agent may have just closed, feeling like it "opened again" rather than
  // landing on a normal pre-start screen with location info.
  const [isViewingLiveMap, setIsViewingLiveMap] = useState(false);
  // Mobile full-screen map mode — the map filling the screen and
  // everything else tucked into a bottom sheet, entered/exited via
  // isViewingLiveMap above rather than being the only possible view.
  const [isSheetExpanded, setIsSheetExpanded] = useState(true);
  useEffect(() => {
    setIsViewingLiveMap(false);
    // Pre-start's sheet starts expanded — its own content is short and
    // ends in the Start Visit button itself, which shouldn't need an extra
    // tap to reach while looking at the map. In-progress's sheet (much
    // longer: turn-by-turn, notes, photos, End Visit) keeps starting
    // collapsed to a thin strip instead.
    setIsSheetExpanded(phase === 'pre-start');
  }, [phase]);

  const [selectedOutcome, setSelectedOutcome] = useState<VisitOutcome | null>(null);
  const [rescheduleHours, setRescheduleHours] = useState('');
  const [reminderMinutes, setReminderMinutes] = useState('');
  const [assignMode, setAssignMode] = useState<'user' | 'department'>('user');
  const [assignedId, setAssignedId] = useState<string | undefined>(undefined);
  const [showAssignPicker, setShowAssignPicker] = useState(false);
  const [outcomeNoteText, setOutcomeNoteText] = useState('');
  const [isEnding, setIsEnding] = useState(false);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async () => {
    if (!visitId) return;
    setIsLoading(true);
    try {
      const [visitRes, configRes] = await Promise.all([
        apiService.getVisit(visitId),
        apiService.getVisitEffectiveConfig(visitId),
      ]);
      const v = (visitRes?.data ?? null) as Visit | null;
      setVisit(v);
      setConfig((configRes?.data ?? null) as EffectiveConfig | null);
      if (v?.status === 'in_progress') setPhase('in-progress');
      else setPhase('pre-start');
    } catch (err) {
      console.warn('[VisitExecute] load error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [visitId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (phase !== 'in-progress') return;
    timerRef.current = setInterval(() => setElapsedNow(Date.now()), 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [phase]);

  const fetchLocation = async () => {
    setIsFetchingLocation(true);
    try {
      const { status } = await Location.requestForegroundPermissionsAsync();
      if (status !== 'granted') {
        showNotification('error', t('visits.execute.title'), t('visits.execute.errors.locationPermissionDenied'));
        return;
      }
      const location = await Location.getCurrentPositionAsync({ accuracy: Location.Accuracy.Balanced });
      setCurrentPosition({
        latitude: location.coords.latitude,
        longitude: location.coords.longitude,
        accuracyMeters: location.coords.accuracy ?? undefined,
      });
    } catch (err) {
      console.warn('[VisitExecute] fetchLocation error:', err);
      showNotification('error', t('visits.execute.title'), t('visits.execute.errors.locationFailed'));
    } finally {
      setIsFetchingLocation(false);
    }
  };

  useEffect(() => {
    // Only fetch/show GPS at all when location enforcement is actually
    // enabled in config — confirmed real bug: this ran unconditionally,
    // so the distance-from-target warning (below) kept appearing even for
    // a business that had turned location enforcement OFF entirely.
    // Reads config?.enforcement directly (not the `enforcement` const
    // declared further down, after this component's early loading-state
    // return) — hooks must run unconditionally before any early return,
    // so this effect executes before that later declaration exists.
    if (phase === 'pre-start' && config?.enforcement?.location.enabled) fetchLocation();
  }, [phase, config?.enforcement?.location.enabled]);

  // Live position watch — starts as soon as there's a route to preview
  // (pre-start, so the agent can see live routing/ETA and get the
  // proactive "you're close" nudge before ever starting the visit), not
  // just once actually in progress. Stops once ending (or the screen
  // navigates away) or when there's no target location to route to at
  // all (hasMap false), so GPS isn't left running for a visit that can't
  // show a map either way.
  useEffect(() => {
    // Inlined rather than reading the shared `hasMap` const below — that's
    // declared after this component's `isLoading || !visit` early return,
    // while this effect (like all hooks) must run before any early return,
    // so `hasMap` isn't in scope yet at this point in the function body.
    const hasTargetLocation = typeof visit?.targetLocation?.latitude === 'number' && typeof visit?.targetLocation?.longitude === 'number';
    if (phase === 'ending' || !hasTargetLocation) {
      locationWatchRef.current?.remove();
      locationWatchRef.current = null;
      return;
    }

    let cancelled = false;
    const start = async () => {
      const { status } = await Location.requestForegroundPermissionsAsync();
      if (status !== 'granted' || cancelled) return;

      // Get an immediate one-time fix in parallel with starting the watch —
      // confirmed real bug: relying on watchPositionAsync alone left
      // effectiveOrigin (and therefore the whole live-directions fetch,
      // map polyline, and turn banner) stuck at null on browsers/devices
      // where the watch's first callback is slow to arrive (observed on
      // desktop Chrome, which can take much longer than mobile GPS for its
      // first fix), even though a position was readily available via a
      // direct one-shot request. This live position is used for the map
      // preview/live-directions from pre-start onward, independent of the
      // separate one-time currentPosition fetch (still gated on
      // enforcement.location.enabled) used for the actual Start-blocking
      // geofence check.
      //
      // Accuracy.High (not Balanced) is required here specifically because
      // of a real bug in expo-location's WEB implementation: it derives the
      // browser's enableHighAccuracy flag as `accuracy > Balanced`, so
      // passing Balanced itself silently evaluates to enableHighAccuracy:
      // false, which makes mobile browsers fall back to cell/WiFi-based
      // positioning instead of GPS — confirmed real-world symptom: the
      // marker stayed static while actually driving, because cell/WiFi
      // fixes update far less often and precisely than GPS. Native
      // (iOS/Android) accuracy enums aren't affected by this, only web.
      Location.getCurrentPositionAsync({ accuracy: Location.Accuracy.High })
        .then((location) => {
          if (cancelled) return;
          setLiveAgentPosition({ latitude: location.coords.latitude, longitude: location.coords.longitude });
        })
        .catch((err) => console.warn('[VisitExecute] initial live position fetch failed:', err));

      locationWatchRef.current = await Location.watchPositionAsync(
        { accuracy: Location.Accuracy.High, timeInterval: 3000, distanceInterval: 10 },
        (location) => {
          if (cancelled) return;
          setLiveAgentPosition({ latitude: location.coords.latitude, longitude: location.coords.longitude });
        }
      );
    };
    start();

    // Confirmed real-world bug specifically on mobile browsers: even with
    // the Accuracy.High fix above, expo-location's web watchPositionAsync
    // shim (a thin wrapper over the browser's own watchPosition, which has
    // no native concept of expo's distanceInterval option) was observed to
    // stop delivering callbacks at all after the first one on some mobile
    // browsers — the map only ever updated after a full page reload, never
    // from live movement. A plain polling fallback, unique to web, works
    // around this by not depending on the watch's callback pipeline at
    // all: every 5s it just asks for a fresh fix directly, the same
    // one-shot call already used above and known to work reliably.
    let pollTimer: ReturnType<typeof setInterval> | null = null;
    if (Platform.OS === 'web') {
      pollTimer = setInterval(() => {
        if (cancelled) return;
        Location.getCurrentPositionAsync({ accuracy: Location.Accuracy.High })
          .then((location) => {
            if (cancelled) return;
            setLiveAgentPosition({ latitude: location.coords.latitude, longitude: location.coords.longitude });
          })
          .catch((err) => console.warn('[VisitExecute] fallback live position poll failed:', err));
      }, 5000);
    }

    return () => {
      cancelled = true;
      if (pollTimer) clearInterval(pollTimer);
      // expo-location@19's web LocationEventEmitter is a plain
      // expo-modules-core EventEmitter, which has no removeSubscription
      // method — but the shared (native+web) Subscriber.unregisterCallback
      // calls exactly that unconditionally, so .remove() throws
      // "removeSubscription is not a function" on web specifically. This is
      // an upstream expo-location bug, not something fixable from app code
      // without patching node_modules; wrapped so a failed unsubscribe
      // (harmless — this effect is tearing down anyway, e.g. on End Visit)
      // doesn't crash the whole screen the way it did before.
      try {
        locationWatchRef.current?.remove();
      } catch (err) {
        console.warn('[VisitExecute] location watch cleanup failed (known expo-location web issue):', err);
      }
      locationWatchRef.current = null;
    };
  }, [phase, visit?.targetLocation?.latitude, visit?.targetLocation?.longitude]);

  // Re-fetches live-directions (distance/duration/road geometry) every time
  // the agent's watched position moves — this is what keeps the polyline
  // and ETA current as they actually travel, from pre-start's route
  // preview through to arrival. Falls back to the pre-start currentPosition
  // (already resolved successfully, or the visit couldn't have started
  // under location enforcement) when the live GPS watch hasn't produced a
  // fix yet — confirmed real bug: this previously waited ONLY for
  // liveAgentPosition, so on a device/browser where watchPositionAsync is
  // slow (or the initial fix simply hasn't arrived yet), the route line
  // never appeared at all even though a perfectly good starting position
  // was already known from pre-start.
  const effectiveOrigin = liveAgentPosition ?? currentPosition;
  useEffect(() => {
    // Same TDZ constraint as the position-watch effect above — `hasMap`
    // isn't in scope yet this early in the function body, so the target-
    // location check is inlined here too.
    const hasTargetLocation = typeof visit?.targetLocation?.latitude === 'number' && typeof visit?.targetLocation?.longitude === 'number';
    if (phase === 'ending' || !hasTargetLocation || !effectiveOrigin || !visitId) return;
    let cancelled = false;
    apiService.getVisitLiveDirections(visitId, effectiveOrigin.latitude, effectiveOrigin.longitude, travelMode)
      .then((res) => {
        if (cancelled || !res.data) return;
        setLiveDirections({
          distanceMeters: res.data.distanceMeters,
          durationSeconds: res.data.durationSeconds,
          geometry: res.data.geometry,
          engineFallbackReason: res.data.engineFallbackReason,
          destination: res.data.destination,
          steps: res.data.steps,
          alternatives: res.data.alternatives,
        });
        // The route the rest of this screen tracks (progress bar, map
        // traveled/remaining split, ETA text) — the primary route by
        // default, or whichever alternative the user tapped. Falls back to
        // the primary if the selected index is out of range (e.g. OSRM
        // stopped offering that many alternatives on a later poll).
        const selected = selectedRouteIndex > 0 ? res.data.alternatives?.[selectedRouteIndex - 1] : undefined;
        const effectiveDistance = selected?.distanceMeters ?? res.data.distanceMeters;
        const effectiveGeometry = selected?.geometry ?? res.data.geometry;

        // First reading for the current phase+travelMode+selectedRoute
        // combination becomes the progress bar's 100% baseline — keyed by
        // that combination (rather than a plain useEffect reset, which
        // raced this same fetch's response depending on effect declaration
        // order) so a travel-mode switch, a fresh in-progress phase, or
        // switching to a different route alternative always captures a NEW
        // baseline on its own very first response, never comparing against
        // a stale one from a different mode/session/route.
        //
        // ALSO re-baselines on a genuine off-route deviation — confirmed
        // real bug: the backend already recalculates a brand-new route from
        // wherever the agent currently is on EVERY poll (getLiveDirections
        // uses the agent's live position as the routing origin), but this
        // baseline previously only ever refreshed once per phase/mode/route
        // combination, so if the agent left the originally-planned road
        // (missed a turn, took a different real-world street), the
        // progress bar's denominator and the map's traveled/remaining split
        // baseline stayed frozen to the ORIGINAL plan — the fresh
        // distance/ETA numbers (effectiveRoute, read directly off
        // liveDirections every poll) were already correct, but the visual
        // route line and progress fraction kept measuring against a road
        // the agent was no longer on. Detected via nearest-point-to-route
        // distance exceeding OFF_ROUTE_METERS, the same "how far off the
        // known route is the agent" check a normal turn-by-turn app uses to
        // trigger a reroute.
        const OFF_ROUTE_METERS = 70;
        const isOffRoute =
          !!originalRouteGeometry &&
          originalRouteGeometry.length > 1 &&
          nearestDistanceToRouteMeters(effectiveOrigin, originalRouteGeometry) > OFF_ROUTE_METERS;

        const baselineKey = `${phase}:${travelMode}:${selectedRouteIndex}`;
        if (initialDistanceBaselineKeyRef.current !== baselineKey || isOffRoute) {
          initialDistanceBaselineKeyRef.current = baselineKey;
          setInitialDistanceMeters(effectiveDistance);
          // Captured at the exact same moment as the distance baseline —
          // this IS the full remaining route as of visit-start, of
          // switching routes, or of a fresh reroute, which becomes the
          // "original" route LiveVisitMap greys out from behind as the
          // agent advances (every later response only ever contains the
          // shrinking remaining segment, so it can't be used as this
          // baseline itself).
          setOriginalRouteGeometry(effectiveGeometry ?? null);
        }
      })
      .catch((err) => console.warn('[VisitExecute] getVisitLiveDirections error:', err));
    return () => { cancelled = true; };
  }, [phase, effectiveOrigin, visitId, travelMode, selectedRouteIndex, visit?.targetLocation?.latitude, visit?.targetLocation?.longitude]);

  // The route every other piece of UI reads as "the current route" —
  // liveDirections' own primary fields when selectedRouteIndex is 0 (the
  // default), or the picked alternative's fields otherwise. Centralizing
  // this here (rather than repeating the selectedRouteIndex-lookup at each
  // of the several render sites that need distance/duration/geometry/steps)
  // means a route switch takes effect everywhere consistently — the map's
  // traveled/remaining split, the progress bar, the ETA text, and the
  // turn-by-turn steps list all follow whichever route is selected.
  const effectiveRoute = useMemo(() => {
    if (!liveDirections) return null;
    if (selectedRouteIndex > 0) {
      const alt = liveDirections.alternatives?.[selectedRouteIndex - 1];
      if (alt) {
        return { distanceMeters: alt.distanceMeters, durationSeconds: alt.durationSeconds, geometry: alt.geometry, steps: undefined as typeof liveDirections.steps };
      }
    }
    return { distanceMeters: liveDirections.distanceMeters, durationSeconds: liveDirections.durationSeconds, geometry: liveDirections.geometry, steps: liveDirections.steps };
  }, [liveDirections, selectedRouteIndex]);

  // Drives the pre-start "you're close — start now?" nudge. Uses the LIVE
  // watched position and the live-resolved destination (a one-time
  // currentPosition fetch only exists when enforcement.location is enabled)
  // so the nudge can appear regardless of
  // whether enforcement is on — it's purely a helpful suggestion, never
  // the thing that actually gates Start (the real, server-checked gate is
  // startBlocked/geofenceError, both untouched by this).
  const isWithinStartRadius = useMemo(() => {
    const radiusMeters = config?.enforcement?.location?.radiusMeters;
    const target = liveDirections?.destination ?? visit?.targetLocation;
    if (!config?.enforcement?.location?.enabled || !radiusMeters || !effectiveOrigin || !target?.latitude || !target?.longitude) {
      return false;
    }
    return haversineMeters(effectiveOrigin.latitude, effectiveOrigin.longitude, target.latitude, target.longitude) <= radiusMeters;
  }, [config?.enforcement?.location?.enabled, config?.enforcement?.location?.radiusMeters, effectiveOrigin, liveDirections?.destination, visit?.targetLocation]);

  // Live in-map turn banner ("Turn left in 90m") — the "current step" is
  // whichever maneuver point the agent is nearest to right now, advanced to
  // the NEXT one once within PASS_THRESHOLD_METERS of it (so the banner
  // moves on to the next instruction instead of repeating one just driven
  // through). OSRM's final step is always a zero-distance "arrive" with no
  // step after it, so once every step is passed this naturally settles on
  // that last index.
  //
  // Confirmed real bug in the previous approach: it scanned steps in order
  // and returned the FIRST one still farther than the threshold — but
  // step[0]'s own location is the trip's DEPART point near the origin, so
  // once the agent has driven more than ~30m from where they started (i.e.
  // almost immediately), step[0] itself is already "far" and the loop
  // returned index 0 on its very first check, forever — the turn-by-turn
  // list never advanced past the first turn no matter how far the agent
  // actually got. Finding the globally NEAREST step instead (steps are
  // monotonically ordered along the route, so nearest-by-distance reliably
  // tracks progress) fixes this regardless of how far along the trip is.
  const PASS_THRESHOLD_METERS = 30;
  const currentStepIndex = useMemo(() => {
    const steps = effectiveRoute?.steps;
    if (!steps || steps.length === 0 || !effectiveOrigin) return -1;
    let nearestIndex = 0;
    let nearestDistance = Infinity;
    for (let i = 0; i < steps.length; i++) {
      const [lat, lon] = steps[i].location;
      const distance = haversineMeters(effectiveOrigin.latitude, effectiveOrigin.longitude, lat, lon);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = i;
      }
    }
    if (nearestDistance <= PASS_THRESHOLD_METERS && nearestIndex < steps.length - 1) {
      return nearestIndex + 1;
    }
    return nearestIndex;
  }, [effectiveRoute?.steps, effectiveOrigin]);
  const currentStep = currentStepIndex >= 0 ? effectiveRoute?.steps?.[currentStepIndex] : undefined;

  const uploadCapturedPhoto = async (target: 'start' | 'end', base64: string, fileName: string) => {
    const uploaded = await apiService.uploadMediaToStorage({
      fileName,
      fileData: base64,
      assetType: 'image',
      businessId: currentBusiness?._id || '',
      campaignName: 'visits',
    });
    const photo: VisitPhoto = {
      url: uploaded.url,
      mediaId: uploaded.mediaId,
      capturedAt: new Date().toISOString(),
      coordinates: currentPosition ? { latitude: currentPosition.latitude, longitude: currentPosition.longitude } : undefined,
      phase: target,
    };
    if (target === 'start') setStartPhotos((prev) => [...prev, photo]);
    else setEndPhotos((prev) => [...prev, photo]);
  };

  const capturePhoto = async (target: 'start' | 'end') => {
    // Web: expo-image-picker's launchCameraAsync is unreliable in-browser
    // (confirmed elsewhere in this app, see the CameraView state's doc
    // comment above) — opens an embedded CameraView modal instead, which
    // takePictureAsync (below) captures a still frame from directly,
    // guaranteeing an actual live camera shot rather than a file picker.
    if (Platform.OS === 'web') {
      setIsCapturingPhoto(true);
      try {
        if (!CameraView) {
          const mod = await import('expo-camera');
          setCameraView(() => mod.CameraView);
          const { status } = await mod.Camera.requestCameraPermissionsAsync();
          const granted = status === 'granted';
          setCameraPermissionGranted(granted);
          if (!granted) {
            showNotification('error', t('visits.execute.title'), t('visits.execute.errors.cameraPermissionDenied'));
            return;
          }
        }
        setShowCameraModal(target);
      } finally {
        setIsCapturingPhoto(false);
      }
      return;
    }

    setIsCapturingPhoto(true);
    try {
      const permission = await ImagePicker.requestCameraPermissionsAsync();
      if (!permission.granted) {
        showNotification('error', t('visits.execute.title'), t('visits.execute.errors.cameraPermissionDenied'));
        return;
      }
      const result = await ImagePicker.launchCameraAsync({ mediaTypes: 'images', allowsEditing: false, quality: 0.8 });
      if (result.canceled || !result.assets?.[0]) return;
      const asset = result.assets[0];
      const fileData = await FileSystem.readAsStringAsync(asset.uri, { encoding: 'base64' });
      const fileName = asset.fileName || `visit-${target}-${Date.now()}.jpg`;
      await uploadCapturedPhoto(target, fileData, fileName);
    } catch (err) {
      console.warn('[VisitExecute] capturePhoto error:', err);
      showNotification('error', t('visits.execute.title'), t('visits.execute.errors.photoUploadFailed'));
    } finally {
      setIsCapturingPhoto(false);
    }
  };

  const handleWebCameraSnapshot = async () => {
    if (!showCameraModal || !cameraRef.current) return;
    setIsCapturingPhoto(true);
    try {
      const picture = await cameraRef.current.takePictureAsync({ base64: true, quality: 0.8 });
      if (!picture?.base64) throw new Error('No image data captured');
      const target = showCameraModal;
      setShowCameraModal(null);
      await uploadCapturedPhoto(target, picture.base64, `visit-${target}-${Date.now()}.jpg`);
    } catch (err) {
      console.warn('[VisitExecute] handleWebCameraSnapshot error:', err);
      showNotification('error', t('visits.execute.title'), t('visits.execute.errors.photoUploadFailed'));
    } finally {
      setIsCapturingPhoto(false);
    }
  };

  const removePhoto = (target: 'start' | 'end', mediaId: string) => {
    if (target === 'start') setStartPhotos((prev) => prev.filter((p) => p.mediaId !== mediaId));
    else setEndPhotos((prev) => prev.filter((p) => p.mediaId !== mediaId));
  };

  const handleStart = async () => {
    // currentPosition is only ever populated when location enforcement is
    // enabled (fetchLocation is no longer auto-run otherwise) — requiring
    // it unconditionally here would make Start permanently unusable for a
    // business that has location enforcement turned OFF entirely.
    const locationRequired = !!config?.enforcement?.location.enabled;
    if (!visitId || (locationRequired && !currentPosition)) return;
    setGeofenceError(null);
    setIsStarting(true);
    try {
      const payload: Record<string, any> = {
        latitude: currentPosition?.latitude,
        longitude: currentPosition?.longitude,
        accuracyMeters: currentPosition?.accuracyMeters,
        photos: startPhotos,
      };
      if (overrideReason.trim()) payload.overrideReason = overrideReason.trim();
      await apiService.startVisit(visitId, payload);
      await load();
    } catch (err: any) {
      const geofence = err?.data?.additionalInfo?.geofence;
      const ongoingVisitId = err?.data?.additionalInfo?.ongoingVisitId;
      if (geofence && typeof geofence.distanceMeters === 'number') {
        setGeofenceError({ distanceMeters: geofence.distanceMeters, radiusMeters: geofence.radiusMeters });
      } else if (ongoingVisitId) {
        setOngoingVisitConflictId(ongoingVisitId);
      } else {
        showNotification('error', t('visits.execute.title'), err?.message ?? t('visits.errors.generic'));
      }
    } finally {
      setIsStarting(false);
    }
  };

  const handleAddNote = async () => {
    if (!visitId || !noteText.trim()) return;
    setIsAddingNote(true);
    try {
      await apiService.addVisitNote(visitId, noteText.trim());
      // The server also writes the note to the contact's notes (tagged
      // [Visit]) — reload so it shows in the contact notes below.
      if (visit?.contactId) {
        apiService.getParentContactById(visit.contactId).then((res) => res?.data && setContact(res.data)).catch(() => {});
      }
      setNoteText('');
    } catch (err: any) {
      showNotification('error', t('visits.execute.title'), err?.message ?? t('visits.errors.generic'));
    } finally {
      setIsAddingNote(false);
    }
  };

  const selectOutcome = (outcome: VisitOutcome) => {
    setSelectedOutcome(outcome);
    setRescheduleHours(String(outcome.action?.config?.defaultOffsetHours ?? ''));
    setReminderMinutes(String(outcome.action?.config?.defaultOffsetMinutes ?? ''));
    setAssignedId(undefined);
    setOutcomeNoteText('');
  };

  const handleConfirmEnd = async () => {
    if (!visitId || !selectedOutcome) return;

    let actionPayload: Record<string, any> | undefined;
    let note: string | undefined;

    if (selectedOutcome.action?.type === 'reschedule') {
      actionPayload = { offsetHours: Number(rescheduleHours) || 0 };
    } else if (selectedOutcome.action?.type === 'reminder') {
      actionPayload = { offsetMinutes: Number(reminderMinutes) || 0 };
    } else if (selectedOutcome.action?.type === 'assign') {
      if (!assignedId) {
        showNotification('warning', t('visits.execute.title'), t('visits.execute.errors.assigneeRequired'));
        return;
      }
      actionPayload = assignMode === 'user' ? { assignedTo: assignedId } : { departmentId: assignedId };
    } else if (selectedOutcome.action?.type === 'note') {
      if (!outcomeNoteText.trim()) {
        showNotification('warning', t('visits.execute.title'), t('visits.execute.errors.noteRequired'));
        return;
      }
      note = outcomeNoteText.trim();
      actionPayload = { noteText: outcomeNoteText.trim() };
    }

    setIsEnding(true);
    try {
      const payload: Record<string, any> = {
        outcomeId: selectedOutcome.id,
        latitude: currentPosition?.latitude,
        longitude: currentPosition?.longitude,
        accuracyMeters: currentPosition?.accuracyMeters,
        photos: endPhotos,
      };
      if (actionPayload) payload.actionPayload = actionPayload;
      if (note) payload.note = note;
      await apiService.endVisit(visitId, payload);
      navigateTo('visit-detail', undefined, { visitId });
    } catch (err: any) {
      showNotification('error', t('visits.execute.title'), err?.message ?? t('visits.errors.generic'));
    } finally {
      setIsEnding(false);
    }
  };

  if (isLoading || !visit) {
    return (
      <ScreenLayout
        header={
          <Header
            showNotifications={true}
            showMessages={true}
            onNotificationPress={() => navigateTo('notifications')}
            onMessagePress={() => navigateTo('chat')}
            showNavigation={Platform.OS === 'web'}
            activeTab={activeTab}
            onTabPress={handleTabPress}
          />
        }
        showBottomTabs={false}
        onLeftPaneClose={() => {}}
        leftPaneContent="settings"
        onSettingsItemPress={async (itemId) => {
          const { setPendingSettingsItem } = await import('../utils/tempStorage');
          await setPendingSettingsItem(itemId);
          navigateTo('settings');
        }}
        showRightPane={false}
        formHeader={<FormHeader title={t('visits.execute.title')} onBack={() => navigateTo('visit-detail', undefined, { visitId })} />}
      >
        <View style={styles.screen}>
          <View style={styles.centered}>
            <ActivityIndicator color={colors.primary as string} />
          </View>
        </View>
      </ScreenLayout>
    );
  }

  const enforcement = config?.enforcement;
  const outcomes = (config?.outcomes || []).filter((o) => o.isActive).sort((a, b) => a.order - b.order);

  // Hard pre-flight block, not just a disabled-after-the-fact backend
  // error — confirmed real UX gap: the Start button was only ever disabled
  // on the location check, so a business with photo enforcement ON let the
  // agent tap Start, wait for the request, and only THEN see a generic
  // error toast for a missing photo, instead of the button being visibly
  // disabled with a clear reason up front (mirroring how the location
  // check already worked).
  const startBlocked =
    (!!enforcement?.location.enabled && !currentPosition) ||
    (!!enforcement?.photos.requireOnStart && startPhotos.length < enforcement.photos.minPhotosOnStart);
  const endBlocked = !!enforcement?.photos.requireOnEnd && endPhotos.length < enforcement.photos.minPhotosOnEnd;

  // Lifted to component scope (rather than declared only inside the
  // in-progress-only IIFE below) so the pre-start phase's own route
  // preview map can reuse the exact same derived values instead of
  // duplicating this logic.
  const hasMap = typeof visit.targetLocation?.latitude === 'number' && typeof visit.targetLocation?.longitude === 'number';

  // Route-picker list — one row per candidate (primary + each
  // alternative), each showing its own distance/ETA so the agent can
  // compare before picking. Only rendered when OSRM actually offered
  // alternatives; a lone primary route (haversine, or OSRM found no
  // meaningfully different path) shows no picker at all, matching how
  // Google/Waze hide the picker when there's nothing to choose between.
  const routeOptions = liveDirections
    ? [
        { index: 0, distanceMeters: liveDirections.distanceMeters, durationSeconds: liveDirections.durationSeconds },
        ...(liveDirections.alternatives ?? []).map((alt, i) => ({
          index: i + 1,
          distanceMeters: alt.distanceMeters,
          durationSeconds: alt.durationSeconds,
        })),
      ]
    : [];

  // Prefers the destination from the most recent live-directions poll
  // (re-resolved from the contact's CURRENT location server-side, see
  // getVisitLiveDirections) over the one-time-fetched visit.targetLocation,
  // so an edited contact location updates the map pin live instead of
  // needing a screen reload. Falls back to visit.targetLocation only for
  // the brief window before the first poll response arrives. Null when
  // there's no target location at all (hasMap false) — this now runs on
  // EVERY render (pre-start included), not just inside an in-progress-only
  // branch, so it can no longer assume visit.targetLocation is present.
  const liveDestination = hasMap
    ? {
        latitude: liveDirections?.destination?.latitude ?? visit.targetLocation!.latitude!,
        longitude: liveDirections?.destination?.longitude ?? visit.targetLocation!.longitude!,
        address: liveDirections?.destination?.address ?? visit.targetLocation!.address,
      }
    : null;

  // Every candidate route EXCEPT whichever one is currently
  // selected/primary — that one is already drawn as the main
  // traveled/remaining line via the `geometry` prop below, so including it
  // here too would draw it twice. Rebuilt as a list of {geometry} entries
  // the map can draw as muted alternative lines, each still tappable to
  // switch.
  const mapAlternativeRoutes = liveDirections
    ? routeOptions.filter((o) => o.index !== selectedRouteIndex).map((o) => {
        const geom = o.index === 0 ? liveDirections.geometry : liveDirections.alternatives?.[o.index - 1]?.geometry;
        return { distanceMeters: o.distanceMeters, durationSeconds: o.durationSeconds, geometry: geom ?? [] };
      })
    : [];
  // Tapping an alternative line on the map re-indexes it back into
  // routeOptions' own numbering (LiveVisitMap only knows the filtered
  // array's own index, not the original 0-based primary+alternatives
  // numbering the rest of this screen uses).
  const handleSelectMapAlternative = (filteredIndex: number) => {
    const target = routeOptions.filter((o) => o.index !== selectedRouteIndex)[filteredIndex];
    if (target) setSelectedRouteIndex(target.index);
  };

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications={true}
          showMessages={true}
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={false}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      showRightPane={false}
      formHeader={
        /* Start/End Visit live here (not as an inline button in the
            scrollable content below) — confirmed real bug: the bottom tab
            bar overlapped/covered inline action buttons on mobile, making
            them unreachable without scrolling past where the eye expects a
            primary action to be. FormHeader's own primary-action slot is
            always visible above that overlap, matching how every other
            screen's main action already lives in its header (VisitDetailScreen's
            Start/Continue/Cancel Visit, for one). */
        <FormHeader
          title={t('visits.execute.title')}
          onBack={() => navigateTo('visit-detail', undefined, { visitId })}
          actions={
            phase === 'pre-start'
              ? [{ icon: 'play', label: t('visits.execute.startVisit'), primary: true, onPress: handleStart, disabled: isStarting || startBlocked, loading: isStarting }]
              : phase === 'in-progress'
              ? [{ icon: 'stop-circle', label: t('visits.execute.endVisit'), primary: true, onPress: () => setPhase('ending'), disabled: endBlocked }]
              : undefined
          }
        />
      }
    >
      <View style={styles.screen}>
      <ScrollView contentContainerStyle={styles.content}>
        <ContactSummaryCard contact={contact} contactName={contactName} onNotify={showNotification} />

        {phase === 'pre-start' && (() => {
          // Everything EXCEPT the map itself — shared by all three layouts
          // below (mobile fullscreen sheet, desktop split panel, plain
          // fallback when there's no map to show at all).
          const preStartControls = (
            <>
              {isWithinStartRadius && (
                <TouchableOpacity
                  style={[styles.startNudgeBanner, (isStarting || startBlocked) && styles.disabledButton]}
                  onPress={handleStart}
                  disabled={isStarting || startBlocked}
                >
                  <Ionicons name="navigate-circle" size={20} color={colors.white as string} />
                  <Text style={styles.startNudgeText}>{t('visits.execute.closeToTarget')}</Text>
                </TouchableOpacity>
              )}

              <Text style={styles.sectionTitle}>{t('visits.execute.targetLocation')}</Text>
              {visit.targetLocation?.address ? <Text style={styles.value}>{visit.targetLocation.address}</Text> : null}

              {/* Mobile: map opens on demand (isViewingLiveMap defaults
                  false now for both phases) — this button is pre-start's
                  equivalent of in-progress's own identical button below. */}
              {!isDesktopWeb && liveDestination && (
                <TouchableOpacity style={styles.viewMapButton} onPress={() => setIsViewingLiveMap(true)}>
                  <Ionicons name="map-outline" size={18} color={colors.primary as string} />
                  <Text style={styles.viewMapButtonText}>{t('visits.execute.viewOnDislixMaps')}</Text>
                </TouchableOpacity>
              )}

              {enforcement?.photos.requireOnStart && (
                <>
                  <Text style={styles.formLabel}>{t('visits.execute.startPhotos')}</Text>
                  <View style={styles.photoRow}>
                    {startPhotos.map((photo) => (
                      <View key={photo.mediaId} style={styles.photoThumbWrap}>
                        <Image source={{ uri: getImageUrlSync(photo.url, apiService.getBaseURL?.() ?? null) }} style={styles.photoThumb} />
                        <TouchableOpacity style={styles.removePhotoButton} onPress={() => removePhoto('start', photo.mediaId)}>
                          <Ionicons name="close-circle" size={18} color={colors.status.error as string} />
                        </TouchableOpacity>
                      </View>
                    ))}
                    <TouchableOpacity style={styles.addPhotoButton} onPress={() => capturePhoto('start')} disabled={isCapturingPhoto}>
                      {isCapturingPhoto ? <ActivityIndicator color={colors.primary as string} /> : <Ionicons name="camera-outline" size={24} color={colors.primary as string} />}
                    </TouchableOpacity>
                  </View>
                </>
              )}

              {enforcement?.location.allowOverrideWithReason && (
                <>
                  <Text style={styles.formLabel}>{t('visits.execute.overrideReason')}</Text>
                  <TextInput
                    style={styles.input}
                    value={overrideReason}
                    onChangeText={setOverrideReason}
                    placeholder={t('visits.execute.overrideReasonPlaceholder')}
                  />
                </>
              )}

              {geofenceError && (
                <View style={styles.errorBox}>
                  <Ionicons name="warning-outline" size={16} color={colors.status.error as string} />
                  <Text style={styles.errorBoxText}>
                    {t('visits.execute.errors.tooFarAway', {
                      distance: Math.round(geofenceError.distanceMeters),
                      radius: geofenceError.radiusMeters,
                    })}
                  </Text>
                </View>
              )}

              {/* The actual Start Visit button lives in the header now (see
                  FormHeader's actions above) — this hint text stays here
                  since the header has no room to explain WHY it's disabled. */}
              {startBlocked && !isStarting && (
                <Text style={styles.blockedHintText}>
                  {!!enforcement?.location.enabled && !currentPosition
                    ? t('visits.execute.blockedNoLocation')
                    : t('visits.execute.blockedMissingStartPhotos', { count: enforcement?.photos.minPhotosOnStart ?? 1 })}
                </Text>
              )}
            </>
          );

          // Mobile: full-screen map on demand (the "View on DISLIX Maps"
          // button in preStartControls above) — same dismissible-overlay
          // mechanism as in-progress's own on-demand map below.
          if (!isDesktopWeb && liveDestination && isViewingLiveMap) {
            return (
              <Modal visible animationType="fade" onRequestClose={() => setIsViewingLiveMap(false)}>
                <View style={styles.mobileExecutionContainer}>
                  <LiveVisitMap
                    agentPosition={effectiveOrigin}
                    destination={liveDestination}
                    geometry={originalRouteGeometry ?? effectiveRoute?.geometry ?? []}
                    alternativeRoutes={mapAlternativeRoutes}
                    onSelectAlternative={handleSelectMapAlternative}
                    travelMode={travelMode}
                    isFullscreen
                    onClose={() => setIsViewingLiveMap(false)}
                  />

                  <View style={[styles.mobileSheet, { paddingBottom: spacing.md + insets.bottom }]}>
                    <TouchableOpacity activeOpacity={0.7} onPress={() => setIsSheetExpanded((v) => !v)}>
                      <View style={styles.mobileSheetHandle} />
                      <View style={styles.mobileSheetCollapsedRow}>
                        <View style={styles.mobileSheetSummaryRow}>
                          <Text style={styles.liveDirectionsInfoText}>{t('visits.execute.title')}</Text>
                          <Ionicons
                            name={isSheetExpanded ? 'chevron-down' : 'chevron-up'}
                            size={18}
                            color={colors.text.secondary as string}
                            style={styles.mobileSheetChevron}
                          />
                        </View>
                      </View>
                    </TouchableOpacity>

                    {isSheetExpanded && (
                      <ScrollView style={styles.mobileSheetExpanded}>{preStartControls}</ScrollView>
                    )}
                  </View>
                </View>
              </Modal>
            );
          }

          // Desktop web: persistent side-by-side split, same pattern as
          // in-progress's own desktop layout.
          if (isDesktopWeb && liveDestination) {
            return (
              <View style={styles.webInProgressRow}>
                <View style={styles.webMapColumn}>
                  <Text style={styles.sectionTitle}>{t('visits.execute.title')}</Text>
                  <LiveVisitMap
                    agentPosition={effectiveOrigin}
                    destination={liveDestination}
                    geometry={originalRouteGeometry ?? effectiveRoute?.geometry ?? []}
                    alternativeRoutes={mapAlternativeRoutes}
                    onSelectAlternative={handleSelectMapAlternative}
                    travelMode={travelMode}
                    height={480}
                  />
                </View>
                <ScrollView style={styles.webSidePanel} contentContainerStyle={styles.webSidePanelContent}>
                  {preStartControls}
                </ScrollView>
              </View>
            );
          }

          // No target location at all — nothing to preview, just the plain
          // controls (this mirrors in-progress's own no-map fallback).
          return <View style={styles.card}>{preStartControls}</View>;
        })()}

        {phase === 'in-progress' && (() => {
          const nextStepPanel = currentStep ? (
            <View style={styles.nextStepPanel}>
              <Ionicons name={maneuverIconName(currentStep.maneuverType, currentStep.maneuverModifier)} size={24} color={colors.white as string} />
              <View style={styles.nextStepTextWrap}>
                <Text style={styles.nextStepDistance}>{formatDistanceLabel(currentStep.distanceMeters)}</Text>
                <Text style={styles.nextStepName} numberOfLines={1}>{currentStep.name || t('visits.execute.unnamedRoad')}</Text>
              </View>
            </View>
          ) : null;

          // Progress fill — starts FULL (100%) the moment in-progress
          // begins, and depletes toward 0% as remaining distance shrinks
          // (a countdown bar, not a fill-up bar) — this is the fraction of
          // the ORIGINAL distance still remaining, not distance already
          // traveled. Clamped both ends: remaining can briefly exceed the
          // baseline right after a travel-mode switch recomputes a longer
          // route before this render's initialDistanceMeters catches up
          // (same render pass, so the clamp — not a crash — is what keeps
          // the bar from rendering >100% for that one frame), and floored
          // at 0 once the agent is at/very near the destination.
          const progressFraction = (() => {
            if (!effectiveRoute || !initialDistanceMeters || initialDistanceMeters <= 0) return null;
            const remaining = Math.max(0, effectiveRoute.distanceMeters);
            return Math.min(1, Math.max(0, remaining / initialDistanceMeters));
          })();

          // Used by the desktop-web split panel and the plain fallback
          // card, where the sheet's inset padding is appropriate — the
          // mobile full-screen layout builds its OWN inline version further
          // below instead of reusing this, since its progress bar needs to
          // span the full device width (a negative margin breaking out of
          // mobileSheet's padding), which doesn't compose with sharing a
          // flex row with the sheet's collapse/expand chevron the way this
          // plain text-only version does here.
          const summaryRow = liveDirections ? (
            <View>
              <View style={styles.liveDirectionsInfoRow}>
                <Ionicons name="navigate-outline" size={14} color={colors.text.secondary as string} />
                <Text style={styles.liveDirectionsInfoText}>
                  {formatDistanceLabel(effectiveRoute!.distanceMeters)}
                  {typeof effectiveRoute!.durationSeconds === 'number' ? ` · ${t('visits.execute.etaApprox', { minutes: Math.round(effectiveRoute!.durationSeconds / 60) })}` : ''}
                </Text>
              </View>
              {progressFraction !== null && (
                <View style={styles.progressTrack}>
                  <View style={[styles.progressFill, { width: `${progressFraction * 100}%` }]} />
                </View>
              )}
            </View>
          ) : null;

          const fallbackNotice = liveDirections?.engineFallbackReason ? (
            <Text style={styles.liveDirectionsFallbackText}>{t('visits.execute.liveDirectionsFallback')}</Text>
          ) : null;

          // Only upcoming steps — confirmed real bug: this previously listed
          // every step in the route regardless of how far the agent had
          // already driven, so a long route's turn-by-turn list stayed full
          // of already-passed instructions the whole trip instead of
          // shrinking down to just what's still ahead. currentStepIndex is
          // already computed as "first step not yet passed" (see its own
          // definition above), so slicing from there is enough — no need to
          // separately detect/exclude passed steps here.
          const upcomingSteps = effectiveRoute?.steps
            ? effectiveRoute.steps.slice(currentStepIndex >= 0 ? currentStepIndex : effectiveRoute.steps.length)
            : [];
          const stepsList = upcomingSteps.length > 0 ? (
            <ScrollView style={styles.directionStepsList} nestedScrollEnabled>
              {upcomingSteps.map((step, index) => (
                <View key={index} style={styles.directionStepRow}>
                  <Ionicons
                    name={maneuverIconName(step.maneuverType, step.maneuverModifier)}
                    size={16}
                    color={colors.text.primary as string}
                    style={styles.directionStepIcon}
                  />
                  <View style={styles.directionStepTextWrap}>
                    <Text style={styles.directionStepText} numberOfLines={2}>
                      {step.name ? step.name : t('visits.execute.unnamedRoad')}
                    </Text>
                    {step.distanceMeters > 0 && (
                      <Text style={styles.directionStepDistance}>{formatDistanceLabel(step.distanceMeters)}</Text>
                    )}
                  </View>
                </View>
              ))}
            </ScrollView>
          ) : null;

          const noteAndPhotosAndEnd = (
            <>
              <Text style={styles.formLabel}>{t('visits.execute.addNoteQuick')}</Text>
              <View style={styles.noteQuickRow}>
                <TextInput
                  style={[styles.input, styles.noteQuickInput]}
                  value={noteText}
                  onChangeText={setNoteText}
                  placeholder={t('visits.detail.notePlaceholder')}
                />
                <TouchableOpacity
                  style={[styles.noteQuickButton, (isAddingNote || !noteText.trim()) && styles.disabledButton]}
                  onPress={handleAddNote}
                  disabled={isAddingNote || !noteText.trim()}
                >
                  <Ionicons name="send" size={16} color={colors.white as string} />
                </TouchableOpacity>
              </View>

              {enforcement?.photos.requireOnEnd && (
                <>
                  <Text style={styles.formLabel}>{t('visits.execute.endPhotos')}</Text>
                  <View style={styles.photoRow}>
                    {endPhotos.map((photo) => (
                      <View key={photo.mediaId} style={styles.photoThumbWrap}>
                        <Image source={{ uri: getImageUrlSync(photo.url, apiService.getBaseURL?.() ?? null) }} style={styles.photoThumb} />
                        <TouchableOpacity style={styles.removePhotoButton} onPress={() => removePhoto('end', photo.mediaId)}>
                          <Ionicons name="close-circle" size={18} color={colors.status.error as string} />
                        </TouchableOpacity>
                      </View>
                    ))}
                    <TouchableOpacity style={styles.addPhotoButton} onPress={() => capturePhoto('end')} disabled={isCapturingPhoto}>
                      {isCapturingPhoto ? <ActivityIndicator color={colors.primary as string} /> : <Ionicons name="camera-outline" size={24} color={colors.primary as string} />}
                    </TouchableOpacity>
                  </View>
                </>
              )}

              {/* The actual End Visit button lives in the header now (see
                  FormHeader's actions above) — same reasoning as pre-start's
                  Start Visit button. */}
              {endBlocked && (
                <Text style={styles.blockedHintText}>
                  {t('visits.execute.blockedMissingEndPhotos', { count: enforcement?.photos.minPhotosOnEnd ?? 1 })}
                </Text>
              )}
            </>
          );

          const map = liveDestination ? (
            <LiveVisitMap
              agentPosition={effectiveOrigin}
              destination={liveDestination}
              geometry={originalRouteGeometry ?? effectiveRoute?.geometry ?? []}
              alternativeRoutes={mapAlternativeRoutes}
              onSelectAlternative={handleSelectMapAlternative}
              travelMode={travelMode}
              height={isMapFullscreen ? undefined : 420}
              isFullscreen={isMapFullscreen}
              onToggleFullscreen={() => setIsMapFullscreen((v) => !v)}
            />
          ) : null;

          // Mobile (native app, or a phone's mobile browser) gets a
          // map-first full-screen mode ON DEMAND (via isViewingLiveMap,
          // toggled by the "View on DISLIX Maps" button in the normal
          // in-progress screen below) rather than automatically — the map
          // fills the whole viewport, and everything else (next-step, full
          // turn-by-turn, note, photos, End Visit) lives in a bottom sheet
          // that starts as a thin collapsed strip (next-step + distance/ETA)
          // and expands on tap. A close button (top-left, mirroring
          // fullscreenButton's own corner elsewhere in this app) returns to
          // the normal in-progress screen without affecting the visit
          // itself — this is purely a view, ending the visit still only
          // happens via the explicit End Visit button either way.
          if (!isDesktopWeb && liveDestination && isViewingLiveMap) {
            return (
              <Modal visible animationType="fade" onRequestClose={() => setIsViewingLiveMap(false)}>
                <View style={styles.mobileExecutionContainer}>
                  <LiveVisitMap
                    agentPosition={effectiveOrigin}
                    destination={liveDestination}
                    geometry={originalRouteGeometry ?? effectiveRoute?.geometry ?? []}
                    alternativeRoutes={mapAlternativeRoutes}
                    onSelectAlternative={handleSelectMapAlternative}
                    travelMode={travelMode}
                    // Deliberately NOT onTravelModeChange here — the mode
                    // picker already lives in mobileTopOverlay above; passing
                    // it here too made LiveVisitMap render its OWN internal
                    // picker as well (bottom-left), a redundant second
                    // control that overlapped/competed with the recenter
                    // button. Only the top-bar picker should exist now.
                    isFullscreen
                    onClose={() => setIsViewingLiveMap(false)}
                  />

                  {/* pointerEvents="box-none" — confirmed real bug: this row
                      spans the full screen width (left/right: spacing.md),
                      and now that the travel-mode picker is gone it has only
                      ONE child (the timer pill) on the left, leaving the
                      rest of its width an invisible but still touchable
                      View sitting at a higher zIndex directly on top of
                      LiveVisitMap's own compass/recenter/fullscreen buttons
                      in the top-right corner — silently swallowing taps
                      meant for them even though nothing was visibly there.
                      box-none lets touches pass through the empty space
                      while the timer pill itself stays tappable/visible. */}
                  <View style={[styles.mobileTopOverlay, { top: spacing.md + insets.top + 60 }]} pointerEvents="box-none">
                    <View style={styles.mobileTimerPill}>
                      <Ionicons name="time-outline" size={14} color={colors.white as string} />
                      <Text style={styles.mobileTimerText}>
                        {visit.startedAt ? formatElapsed(elapsedNow - new Date(visit.startedAt).getTime()) : '00:00'}
                      </Text>
                    </View>
                  </View>

                  <View style={[styles.mobileSheet, { paddingBottom: spacing.md + insets.bottom }]}>
                    {/* Only this collapsed strip toggles expand/collapse —
                        confirmed real bug: when the whole sheet (including
                        the expanded content below) was one TouchableOpacity,
                        tapping the note input or End Visit button also
                        fired the collapse toggle underneath it. */}
                    <TouchableOpacity
                      activeOpacity={0.7}
                      onPress={() => setIsSheetExpanded((v) => !v)}
                    >
                      <View style={styles.mobileSheetHandle} />
                      {/* Stacked, not a horizontal row — confirmed real bug:
                          the colored next-step banner, the plain-text
                          distance/ETA summary, and the chevron icon
                          previously all competed for the same row with no
                          width allocated between them, squeezing one or
                          more out entirely on a phone-width screen. */}
                      <View style={styles.mobileSheetCollapsedRow}>
                        {nextStepPanel ? (
                          <View style={styles.mobileSheetNextStepWrap}>{nextStepPanel}</View>
                        ) : (
                          <View style={[styles.nextStepPanel, styles.mobileSheetNextStepWrap]}>
                            <Ionicons name="navigate" size={24} color={colors.white as string} />
                            <View style={styles.nextStepTextWrap}>
                              <Text style={styles.nextStepDistance}>{t('visits.execute.onTheWay')}</Text>
                            </View>
                          </View>
                        )}
                        {/* The progress bar (inside mobileSummaryRow) needs
                            to span the FULL sheet width, so it can't live
                            inside the same flex row as the small chevron
                            icon the way the plain text summary can — the
                            chevron gets its own compact row alongside just
                            the distance/ETA text, and the full-width bar
                            renders as mobileSummaryRow's own separate line
                            underneath both. */}
                        <View style={styles.mobileSheetSummaryRow}>
                          {effectiveRoute && (
                            <View style={styles.liveDirectionsInfoRow}>
                              <Ionicons name="navigate-outline" size={14} color={colors.text.secondary as string} />
                              <Text style={styles.liveDirectionsInfoText}>
                                {formatDistanceLabel(effectiveRoute.distanceMeters)}
                                {typeof effectiveRoute.durationSeconds === 'number' ? ` · ${t('visits.execute.etaApprox', { minutes: Math.round(effectiveRoute.durationSeconds / 60) })}` : ''}
                              </Text>
                            </View>
                          )}
                          <Ionicons
                            name={isSheetExpanded ? 'chevron-down' : 'chevron-up'}
                            size={18}
                            color={colors.text.secondary as string}
                            style={styles.mobileSheetChevron}
                          />
                        </View>
                        {progressFraction !== null && (
                          <View style={[styles.progressTrack, styles.progressTrackFullWidth]}>
                            <View style={[styles.progressFill, { width: `${progressFraction * 100}%` }]} />
                          </View>
                        )}
                      </View>
                    </TouchableOpacity>

                    {isSheetExpanded && (
                      <ScrollView style={styles.mobileSheetExpanded}>
                        {fallbackNotice}
                        {stepsList}
                        <View style={styles.webSidePanelDivider} />
                        {noteAndPhotosAndEnd}
                      </ScrollView>
                    )}
                  </View>
                </View>
              </Modal>
            );
          }

          // Desktop web gets a persistent side-by-side split (map left,
          // directions + visit actions right) so the map can be much larger
          // without pushing the note/photo/end-visit controls off-screen —
          // native AND mobile-web (a phone's browser, which is also
          // Platform.OS==='web' but far too narrow for two columns) keep
          // everything stacked in one scroll instead.
          if (isDesktopWeb && hasMap && !isMapFullscreen) {
            return (
              <View style={styles.webInProgressRow}>
                <View style={styles.webMapColumn}>
                  <Text style={styles.sectionTitle}>{t('visits.execute.inProgress')}</Text>
                  <Text style={styles.elapsedText}>
                    {visit.startedAt ? formatElapsed(elapsedNow - new Date(visit.startedAt).getTime()) : '00:00'}
                  </Text>
                  {map}
                </View>
                <ScrollView style={styles.webSidePanel} contentContainerStyle={styles.webSidePanelContent}>
                  {nextStepPanel}
                  {summaryRow}
                  {fallbackNotice}
                  {stepsList}
                  <View style={styles.webSidePanelDivider} />
                  {noteAndPhotosAndEnd}
                </ScrollView>
              </View>
            );
          }

          return (
            <View style={styles.card}>
              <Text style={styles.sectionTitle}>{t('visits.execute.inProgress')}</Text>
              <Text style={styles.elapsedText}>
                {visit.startedAt ? formatElapsed(elapsedNow - new Date(visit.startedAt).getTime()) : '00:00'}
              </Text>

              {hasMap && (
                <>
                  {isDesktopWeb ? (
                    <View style={isMapFullscreen ? styles.liveMapWrapFullscreen : styles.liveMapWrap}>
                      {map}
                    </View>
                  ) : (
                    // Mobile: the map itself only shows on demand (see
                    // isViewingLiveMap's own doc comment above) — once
                    // started, the agent is assumed to already be there, so
                    // this button replaces the automatic full-screen map,
                    // while next-step/ETA/turn-by-turn text below still
                    // shows without needing the map open at all.
                    <TouchableOpacity style={styles.viewMapButton} onPress={() => setIsViewingLiveMap(true)}>
                      <Ionicons name="map-outline" size={18} color={colors.primary as string} />
                      <Text style={styles.viewMapButtonText}>{t('visits.execute.viewOnDislixMaps')}</Text>
                    </TouchableOpacity>
                  )}
                  {!isMapFullscreen && (
                    <>
                      {nextStepPanel}
                      {summaryRow}
                      {fallbackNotice}
                      {stepsList}
                    </>
                  )}
                </>
              )}

              {!isMapFullscreen && noteAndPhotosAndEnd}
            </View>
          );
        })()}

        {phase === 'ending' && (
          <View style={styles.card}>
            <Text style={styles.sectionTitle}>{t('visits.execute.selectOutcome')}</Text>
            <View style={styles.outcomesGrid}>
              {outcomes.map((outcome) => {
                const active = selectedOutcome?.id === outcome.id;
                return (
                  <TouchableOpacity
                    key={outcome.id}
                    style={[styles.outcomeChip, { borderColor: outcome.color || (colors.primary as string) }, active && { backgroundColor: outcome.color || (colors.primary as string) }]}
                    onPress={() => selectOutcome(outcome)}
                  >
                    <Ionicons name={(outcome.icon as any) || 'ellipse-outline'} size={16} color={active ? (colors.white as string) : (outcome.color || (colors.text.primary as string))} />
                    <Text style={[styles.outcomeChipText, active && { color: colors.white as string }]}>{outcome.label}</Text>
                  </TouchableOpacity>
                );
              })}
            </View>

            {selectedOutcome?.action?.type === 'reschedule' && (
              <>
                <Text style={styles.formLabel}>{t('visits.execute.rescheduleHours')}</Text>
                <TextInput style={styles.input} keyboardType="number-pad" value={rescheduleHours} onChangeText={setRescheduleHours} />
              </>
            )}
            {selectedOutcome?.action?.type === 'reminder' && (
              <>
                <Text style={styles.formLabel}>{t('visits.execute.reminderMinutes')}</Text>
                <TextInput style={styles.input} keyboardType="number-pad" value={reminderMinutes} onChangeText={setReminderMinutes} />
              </>
            )}
            {selectedOutcome?.action?.type === 'assign' && (
              <>
                <View style={styles.segmentedRow}>
                  <TouchableOpacity
                    style={[styles.segmentButton, assignMode === 'user' && styles.segmentButtonActive]}
                    onPress={() => setAssignMode('user')}
                  >
                    <Text style={[styles.segmentButtonText, assignMode === 'user' && styles.segmentButtonTextActive]}>{t('visits.create.assignToUser')}</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.segmentButton, assignMode === 'department' && styles.segmentButtonActive]}
                    onPress={() => setAssignMode('department')}
                  >
                    <Text style={[styles.segmentButtonText, assignMode === 'department' && styles.segmentButtonTextActive]}>{t('visits.create.assignToDepartment')}</Text>
                  </TouchableOpacity>
                </View>
                <TouchableOpacity style={styles.pickerButton} onPress={() => setShowAssignPicker(true)}>
                  <Text style={styles.pickerButtonText}>{assignedId || t('visits.execute.selectAssignee')}</Text>
                </TouchableOpacity>
              </>
            )}
            {selectedOutcome?.action?.type === 'note' && (
              <>
                <Text style={styles.formLabel}>{t('visits.execute.outcomeNote')}</Text>
                <TextInput style={styles.input} value={outcomeNoteText} onChangeText={setOutcomeNoteText} multiline />
              </>
            )}

            <TouchableOpacity
              style={[styles.primaryButton, (isEnding || !selectedOutcome) && styles.disabledButton]}
              onPress={handleConfirmEnd}
              disabled={isEnding || !selectedOutcome}
            >
              {isEnding ? <ActivityIndicator color={colors.white as string} /> : <Text style={styles.primaryButtonText}>{t('visits.execute.confirm')}</Text>}
            </TouchableOpacity>
          </View>
        )}

        {/* Contact's own persistent notes — same section VisitDetailScreen
            shows, now also available during execution (pre-start,
            in-progress, and ending), not just before committing to Start. */}
        {!!contact?._id && (
          <View style={styles.card}>
            <NotesSection
              notes={contact.notes || []}
              onAddNote={handleAddContactNote}
              onDeleteNote={handleDeleteContactNote}
              placeholder={t('visits.detail.contactNotePlaceholder')}
              title={t('visits.detail.notes')}
              currentUserId={currentUser?.id}
            />
          </View>
        )}
      </ScrollView>
      </View>

      {/* One active visit at a time — the server rejects Start with
          ongoingVisitId when this agent already has a different visit
          in_progress; this offers the one sensible next step (go end it)
          instead of a bare error toast. */}
      <ConfirmationModal
        visible={!!ongoingVisitConflictId}
        onClose={() => setOngoingVisitConflictId(null)}
        onConfirm={() => {
          const targetId = ongoingVisitConflictId;
          setOngoingVisitConflictId(null);
          if (targetId) navigateTo('visit-detail', undefined, { visitId: targetId });
        }}
        title={t('visits.execute.ongoingVisitTitle')}
        message={t('visits.execute.ongoingVisitMessage')}
        confirmText={t('visits.execute.ongoingVisitAction')}
        cancelText={t('common.cancel')}
        type="warning"
      />

      <BusinessUserPicker
        visible={showAssignPicker}
        onClose={() => setShowAssignPicker(false)}
        onConfirm={(ids) => {
          setAssignedId(ids[0]);
          setShowAssignPicker(false);
        }}
        mode="single"
        source={assignMode === 'user' ? 'users' : 'departments'}
        title={t('visits.execute.selectAssignee')}
      />

      {/* Web-only embedded camera (see capturePhoto's doc comment) — native
          uses ImagePicker.launchCameraAsync directly instead, which works
          correctly there. */}
      {Platform.OS === 'web' && (
        <Modal visible={!!showCameraModal} animationType="fade" onRequestClose={() => setShowCameraModal(null)}>
          <View style={styles.cameraModalContainer}>
            {CameraView && cameraPermissionGranted ? (
              <>
                <CameraView ref={cameraRef} style={styles.cameraModalView} facing="back" />
                <View style={styles.cameraModalControls}>
                  <TouchableOpacity style={styles.cameraModalCancel} onPress={() => setShowCameraModal(null)}>
                    <Ionicons name="close" size={22} color={colors.white as string} />
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.cameraModalShutter, isCapturingPhoto && styles.disabledButton]}
                    onPress={handleWebCameraSnapshot}
                    disabled={isCapturingPhoto}
                  >
                    {isCapturingPhoto ? <ActivityIndicator color={colors.white as string} /> : <View style={styles.cameraModalShutterInner} />}
                  </TouchableOpacity>
                  <View style={{ width: 44 }} />
                </View>
              </>
            ) : (
              <View style={styles.cameraModalCentered}>
                <ActivityIndicator color={colors.white as string} />
              </View>
            )}
          </View>
        </Modal>
      )}
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  screen: { flex: 1 },
  content: { padding: spacing.lg, paddingBottom: spacing.xl * 2 },
  centered: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: spacing.xl },

  card: {
    backgroundColor: colors.background.surface as string,
    borderRadius: 12,
    padding: spacing.lg,
    borderWidth: 1,
    borderColor: colors.border as string,
    marginBottom: spacing.lg,
  },
  sectionTitle: {
    fontSize: typography.fontSize.xl,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary as string,
    marginBottom: spacing.sm,
  },
  formLabel: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary as string,
    marginBottom: spacing.xs,
    marginTop: spacing.sm,
  },
  value: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.regular, color: colors.text.primary as string, marginBottom: spacing.xs },
  input: {
    borderWidth: 1,
    borderColor: colors.border as string,
    borderRadius: 8,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.primary as string,
    backgroundColor: colors.background.surface as string,
    marginBottom: spacing.sm,
  },

  photoRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm, marginBottom: spacing.sm },
  photoThumbWrap: { position: 'relative' },
  photoThumb: { width: 64, height: 64, borderRadius: 8, backgroundColor: colors.background.secondary as string },
  removePhotoButton: { position: 'absolute', top: -6, right: -6, backgroundColor: colors.white as string, borderRadius: 10 },
  addPhotoButton: {
    width: 64,
    height: 64,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border as string,
    borderStyle: 'dashed',
    alignItems: 'center',
    justifyContent: 'center',
  },

  errorBox: { flexDirection: 'row', alignItems: 'flex-start', gap: spacing.xs, marginBottom: spacing.sm },
  errorBoxText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.regular, color: colors.status.error as string, flex: 1 },
  blockedHintText: { fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.regular, color: colors.status.error as string, marginTop: spacing.xs, textAlign: 'center' },

  primaryButton: { backgroundColor: colors.primary as string, borderRadius: 12, paddingVertical: spacing.md, alignItems: 'center', marginTop: spacing.md },
  primaryButtonText: { color: colors.white as string, fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold },
  disabledButton: { opacity: 0.6 },

  elapsedText: { fontSize: typography.fontSize['2xl'], fontFamily: typography.fontFamily.bold, color: colors.text.primary as string, marginBottom: spacing.md },

  liveMapWrap: { marginBottom: spacing.md },
  liveMapWrapFullscreen: { flex: 1 },
  viewMapButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    borderWidth: 1,
    borderColor: colors.primary as string,
    borderRadius: 10,
    paddingVertical: spacing.sm,
    marginBottom: spacing.md,
  },
  viewMapButtonText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold, color: colors.primary as string },
  liveDirectionsInfoRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, marginTop: spacing.sm },
  liveDirectionsInfoText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold, color: colors.text.primary as string },
  progressTrack: {
    height: 6,
    borderRadius: 3,
    backgroundColor: colors.border as string,
    marginTop: spacing.xs,
    overflow: 'hidden',
  },
  // Cancels mobileSheet's paddingHorizontal so the track spans the full
  // device width instead of sitting inset like the text/icons around it.
  progressTrackFullWidth: {
    marginHorizontal: -spacing.md,
    borderRadius: 0,
  },
  progressFill: {
    height: '100%',
    borderRadius: 3,
    backgroundColor: colors.primary as string,
  },
  liveDirectionsFallbackText: { fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.regular, color: colors.text.secondary as string, marginTop: spacing.xs },
  directionStepsList: { maxHeight: 180, marginTop: spacing.sm, borderTopWidth: 1, borderTopColor: colors.border as string },
  directionStepRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: spacing.sm, borderBottomWidth: 1, borderBottomColor: colors.border as string, gap: spacing.sm },
  directionStepIcon: { width: 20, textAlign: 'center' },
  directionStepTextWrap: { flex: 1 },
  directionStepText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.medium, color: colors.text.primary as string },
  directionStepDistance: { fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.regular, color: colors.text.secondary as string, marginTop: 2 },


  nextStepPanel: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    backgroundColor: colors.primary as string,
    borderRadius: 10,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    marginTop: spacing.sm,
  },

  startNudgeBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    backgroundColor: colors.status.success as string,
    borderRadius: 10,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    marginTop: spacing.sm,
  },
  startNudgeText: { color: colors.white as string, fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold, flex: 1 },
  nextStepTextWrap: { flex: 1 },
  nextStepDistance: { color: colors.white as string, fontSize: typography.fontSize.lg, fontFamily: typography.fontFamily.bold },
  nextStepName: { color: 'rgba(255,255,255,0.85)', fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.regular, marginTop: 2 },

  // Web-only side-by-side in-progress layout: map on the left (flex:2, much
  // larger than the old stacked-card layout allowed), directions + visit
  // actions (note/photos/end-visit) in a persistent scrollable right pane —
  // native has no room for two columns so it keeps the single-column stack.
  webInProgressRow: { flexDirection: 'row', gap: spacing.lg, minHeight: 520 },
  webMapColumn: { flex: 2 },
  webSidePanel: {
    flex: 1,
    maxWidth: 380,
    backgroundColor: colors.background.primary as string,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.border as string,
  },
  webSidePanelContent: { padding: spacing.md },
  webSidePanelDivider: { height: 1, backgroundColor: colors.border as string, marginVertical: spacing.md },

  // Mobile full-screen in-progress mode — map fills the whole modal, a thin
  // top overlay carries the elapsed timer + travel-mode picker, and a
  // bottom sheet (collapsed strip by default) carries everything else.
  mobileExecutionContainer: { flex: 1, backgroundColor: '#000' },
  mobileTopOverlay: {
    position: 'absolute',
    // Keep the timer below the map's shared Close directions control.
    left: spacing.md,
    right: spacing.md,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    // Confirmed real bug: LiveVisitMap's own fullscreenContainer uses
    // position:'fixed'/'absolute' with zIndex:1000 — a zIndex of 10 here
    // was completely painted over by it, making this whole overlay
    // (and the bottom sheet below) invisible despite rendering correctly
    // in the tree. Both need to clear that 1000 explicitly.
    zIndex: 1001,
    elevation: 1001,
  },
  mobileTimerPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: 'rgba(15, 23, 42, 0.85)',
    borderRadius: 16,
    paddingVertical: 6,
    paddingHorizontal: spacing.sm,
  },
  mobileTimerText: { color: colors.white as string, fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold },
  mobileSheet: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: colors.background.primary as string,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingTop: spacing.xs,
    paddingHorizontal: spacing.md,
    // paddingBottom set inline per-render (spacing.md + insets.bottom) —
    // see call site — so the sheet's own content clears the home-indicator
    // safe area instead of sitting flush against it.
    maxHeight: '75%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    // Same fix as mobileTopOverlay above — must clear LiveVisitMap's
    // fullscreenContainer zIndex:1000, which was otherwise painting over
    // this entire sheet despite it rendering correctly in the tree.
    zIndex: 1001,
    elevation: 1001,
  },
  mobileSheetHandle: {
    width: 40,
    height: 4,
    borderRadius: 2,
    backgroundColor: colors.border as string,
    alignSelf: 'center',
    marginBottom: spacing.sm,
  },
  mobileSheetCollapsedRow: { gap: spacing.xs },
  mobileSheetNextStepWrap: { width: '100%' },
  mobileSheetSummaryRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  mobileSheetChevron: { marginLeft: spacing.xs },
  mobileSheetExpanded: { marginTop: spacing.sm, maxHeight: 420 },

  noteQuickRow: { flexDirection: 'row', gap: spacing.sm, alignItems: 'center' },
  noteQuickInput: { flex: 1, marginBottom: 0 },
  noteQuickButton: { backgroundColor: colors.primary as string, borderRadius: 8, padding: spacing.sm },

  outcomesGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm, marginBottom: spacing.sm },
  outcomeChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    borderWidth: 1.5,
    borderRadius: 16,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.md,
  },
  outcomeChipText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold, color: colors.text.primary as string },

  segmentedRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs, marginBottom: spacing.sm },
  segmentButton: {
    borderWidth: 1,
    borderColor: colors.border as string,
    borderRadius: 8,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
  },
  segmentButtonActive: { backgroundColor: colors.primary as string, borderColor: colors.primary as string },
  segmentButtonText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.regular, color: colors.text.primary as string },
  segmentButtonTextActive: { color: colors.white as string, fontFamily: typography.fontFamily.semibold },

  pickerButton: {
    borderWidth: 1,
    borderColor: colors.border as string,
    borderRadius: 8,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    alignItems: 'center',
    marginBottom: spacing.sm,
  },
  pickerButtonText: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.primary as string },

  cameraModalContainer: { flex: 1, backgroundColor: '#000' },
  cameraModalView: { flex: 1 },
  cameraModalCentered: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  cameraModalControls: {
    position: 'absolute',
    bottom: spacing.xl,
    left: 0,
    right: 0,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.xl,
  },
  cameraModalCancel: { width: 44, height: 44, alignItems: 'center', justifyContent: 'center' },
  cameraModalShutter: {
    width: 72,
    height: 72,
    borderRadius: 36,
    borderWidth: 4,
    borderColor: colors.white as string,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cameraModalShutterInner: { width: 56, height: 56, borderRadius: 28, backgroundColor: colors.white as string },
});

/**
 * The visit page (/visit/:id/execute): the contact's full profile (360) with
 * this visit already open in its visit workspace — directions, rules,
 * start, actions (reminder, list, assignment, quotation, order, vendor,
 * notes) and End visit with the outcome, all next to the contact's history.
 * "Turn-by-turn" opens the classic full-screen navigation for this visit.
 */
export const VisitContactPage: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, routeParams, showNotification, activeTab, setActiveTab } = useAppContext();
  const { t } = useLocale();
  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);
  const { width } = useWindowDimensions();
  const visitId: string | undefined = routeParams?.visitId;
  const [contact, setContact] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setFailed(false);
      try {
        const v = await apiService.getVisit(visitId!);
        const contactId = v?.data?.contactId;
        if (!contactId) throw new Error('no contact');
        const res = await apiService.getParentContactById(contactId);
        const parent = res?.data;
        if (!parent) throw new Error('no contact');
        // ContactEditForm's Contact shape — the omni contact nested under
        // .parentContact (same mapping ConversationScreen uses).
        const ids = parent.identifiers || {};
        const phone = parent.profile?.phone || (Array.isArray(ids) ? ids.find((i: any) => i.type === 'phone' || i.type === 'whatsapp')?.value : ids.phone?.[0] || ids.whatsapp?.[0] || ids.phone || ids.whatsapp);
        const email = parent.profile?.email || (Array.isArray(ids) ? ids.find((i: any) => i.type === 'email')?.value : ids.email?.[0] || ids.email);
        if (!cancelled) {
          setContact({
            id: parent._id || parent.id,
            name: parent.profile?.name || '',
            channel: 'whatsapp',
            phoneNumber: typeof phone === 'string' ? phone : undefined,
            email: typeof email === 'string' ? email : undefined,
            profilePictureUrl: parent.profile?.avatar,
            parentContact: parent,
          });
        }
      } catch {
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [visitId]);

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications={true}
          showMessages={true}
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={false}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      showRightPane={false}
      formHeader={
        <FormHeader
          title={contact?.name || t('visitWorkspace.title')}
          onBack={() => navigateTo('visits')}
          actions={[{
            icon: 'navigate-circle-outline',
            label: t('visitWorkspace.turnByTurn'),
            onPress: () => navigateTo('visit-execute', undefined, { visitId, classic: true }),
          }]}
        />
      }
    >
      <View style={styles.screen}>
        {loading ? (
          <View style={styles.centered}>
            <ActivityIndicator color={colors.primary as string} />
          </View>
        ) : failed || !contact ? (
          <View style={styles.centered}>
            <Text style={{ color: colors.text.secondary as string }}>{t('visits.errors.generic')}</Text>
          </View>
        ) : (
          <ContactEditForm
            contact={contact}
            initialActiveVisitId={visitId}
            compact={Platform.OS === 'web' && width >= designTokens.breakpoints.desktop}
            onClose={() => navigateTo('visits')}
            // Ended → the visit's record; panel just closed → back to the list.
            onVisitClosed={(ended) => (ended ? navigateTo('visit-detail', undefined, { visitId, record: true }) : navigateTo('visits'))}
            onSave={(updated: any) => {
              setContact(updated);
              showNotification('success', t('common.success'), t('common.save'));
            }}
            onDelete={() => navigateTo('visits')}
          />
        )}
      </View>
    </ScreenLayout>
  );
};

export const VisitExecuteScreen: React.FC = () => {
  const { routeParams } = useAppContext();
  return routeParams?.classic ? <VisitExecuteClassicScreen /> : <VisitContactPage />;
};
